import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Github, ArrowRight, ArrowUpRight, Copy, Check, RotateCcw, FileCode2, Terminal } from 'lucide-react';
import { Section } from './ui/Section';

const REPO_URL = 'https://github.com/Rakshan001/elmory';
const SITE_URL = 'https://elmory.vercel.app';
const CASE_STUDY_URL = '/project/13/elmory';
const INSTALL = 'git clone https://github.com/Rakshan001/elmory.git\ncd elmory\nnpm install && npm run build && npm link';

const STATES = [
  { name: 'holds', detail: 'every quote is still in the code', served: true },
  { name: 'suspect', detail: 'a quoted line changed', served: false },
  { name: 'stale', detail: 'the quoted file was deleted', served: false },
  { name: 'note', detail: 'a decision, labelled not code-checked', served: true },
];

const INTERFACES = [
  { label: 'CLI', value: 'remember, note, recall, verify, forget' },
  { label: 'MCP', value: '9 tools over stdio' },
  { label: 'Agents', value: 'Claude Code, Cursor, Codex and any MCP client' },
  { label: 'Storage', value: 'Markdown files in .elmory/, with a SQLite full-text cache that runs offline' },
];

/** The core idea in one interaction: change the quoted line and the fact stops being served. */
function RecallDemo() {
  const [changed, setChanged] = useState(false);
  const value = changed ? '30' : '15';

  return (
    <div className="rounded-xl border border-[var(--baton-hairline)] bg-[var(--bg-elevated)] overflow-hidden">
      {/* Code */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-[var(--baton-hairline)] px-4 py-2.5">
        <span className="baton-mono flex items-center gap-1.5 text-xs text-[var(--fg-secondary)]">
          <FileCode2 size={13} /> src/auth/session.ts
        </span>
        <button
          type="button"
          onClick={() => setChanged((c) => !c)}
          className="baton-mono inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-[var(--baton-hairline)] px-2.5 py-1 text-[11px] text-[var(--fg-primary)] transition-colors hover:bg-[var(--bg-hover)] cursor-pointer"
        >
          {changed ? <><RotateCcw size={12} /> Undo the change</> : 'A teammate edits this line'}
        </button>
      </div>
      <pre className="baton-mono whitespace-pre-wrap break-words px-4 py-4 text-[13px] leading-relaxed text-[var(--fg-secondary)]">
        <span className="text-[var(--fg-muted)]">12  </span>
        <span className={`px-1 transition-colors ${changed ? 'bg-[color-mix(in_srgb,var(--elmory-warn)_16%,transparent)]' : 'bg-[color-mix(in_srgb,var(--elmory-ok)_14%,transparent)]'}`}>
          export const SESSION_TTL_MINUTES = <span className="text-[var(--fg-primary)] font-semibold">{value}</span>;
        </span>
      </pre>

      {/* Recall */}
      <div className="border-t border-[var(--baton-hairline)] px-4 py-4 space-y-2.5">
        <p className="baton-mono flex items-center gap-1.5 text-xs text-[var(--fg-secondary)]">
          <Terminal size={13} /> elmory recall &quot;session timeout&quot;
        </p>
        <div aria-live="polite">
          {!changed ? (
            <div className="border-l-2 border-[var(--elmory-ok)] pl-3 py-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="baton-mono text-[11px] uppercase tracking-wider text-[var(--elmory-ok)]">
                  Holds, served
                </span>
                <span className="text-sm font-medium text-[var(--fg-primary)]">Sessions expire after 15 minutes</span>
              </div>
              <p className="baton-mono mt-1.5 text-[11px] text-[var(--fg-secondary)]">
                evidence unchanged, quotes src/auth/session.ts:12
              </p>
            </div>
          ) : (
            <div className="border-l-2 border-[var(--elmory-warn)] pl-3 py-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="baton-mono text-[11px] uppercase tracking-wider text-[var(--elmory-warn)]">
                  Suspect, withheld
                </span>
                <span className="text-sm font-medium text-[var(--fg-primary)] line-through decoration-[var(--fg-muted)]">
                  Sessions expire after 15 minutes
                </span>
              </div>
              <p className="baton-mono mt-1.5 text-[11px] text-[var(--fg-secondary)]">
                I don&apos;t know. The quoted line changed, so read the code instead. (exit 4)
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ElmorySpotlight() {
  const reduceMotion = useReducedMotion();
  const [copied, setCopied] = useState(false);

  const reveal = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 20 },
    visible: { opacity: 1, y: 0, transition: { duration: reduceMotion ? 0 : 0.5, ease: 'easeOut' } },
  };

  const copyInstall = () => {
    try {
      navigator.clipboard.writeText(INSTALL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const btnBase =
    'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-colors';

  return (
    <Section id="elmory" className="baton-scope relative overflow-hidden pt-4 md:pt-6" aria-labelledby="elmory-title">
      <motion.div
        variants={reveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
        className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--baton-hairline)] pb-5"
      >
        <p className="baton-mono text-xs tracking-[0.22em] text-[var(--fg-primary)] uppercase">// new / open source</p>
        <p className="baton-mono text-xs tracking-[0.14em] text-[var(--fg-secondary)] uppercase">MIT / TypeScript / Node 24+</p>
      </motion.div>

      <div className="grid grid-cols-1 gap-10 pt-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-14">
        {/* Story, states and actions */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="flex min-w-0 flex-col"
        >
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 id="elmory-title" className="baton-display text-5xl font-semibold tracking-tight text-[var(--fg-primary)] sm:text-6xl">
              Elmory
            </h2>
            <span className="baton-mono text-xs uppercase tracking-[0.14em] text-[var(--fg-secondary)]">Pre-release</span>
          </div>

          <p className="baton-display mt-5 text-2xl leading-tight font-medium text-[var(--fg-primary)] sm:text-[2.1rem]">
            Memory for AI coding agents that knows when it&apos;s out of date.
          </p>
          <p className="mt-6 text-base leading-relaxed text-[var(--fg-secondary)]">
            Agent memory goes stale silently. A note says sessions last 15 minutes, a teammate changes it to 30,
            and the next agent still writes code for 15. In Elmory every fact quotes the exact line of code that
            proves it. Each recall checks that the quote is still there. If it is, the fact is served. If the line
            changed or the file is gone, the fact is withheld, and when nothing holds the answer is
            &ldquo;I don&apos;t know&rdquo; rather than a guess.
          </p>

          <dl className="mt-6 border-t border-[var(--baton-hairline)]">
            {STATES.map((st) => (
              <div
                key={st.name}
                className="grid grid-cols-[4.75rem_minmax(0,1fr)_auto] items-baseline gap-3 border-b border-[var(--baton-hairline)] py-2.5 sm:grid-cols-[5.5rem_minmax(0,1fr)_auto]"
              >
                <dt className="baton-mono text-xs font-medium text-[var(--fg-primary)]">{st.name}</dt>
                <dd className="text-sm text-[var(--fg-secondary)]">{st.detail}</dd>
                <dd className="baton-mono text-[11px] uppercase tracking-wider text-[var(--fg-muted)]">
                  {st.served ? 'served' : 'withheld'}
                </dd>
              </div>
            ))}
          </dl>

          <div className="mt-8 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
            <a
              href={SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`${btnBase} bg-[var(--fg-primary)] text-[var(--bg-canvas)] hover:opacity-90`}
            >
              Visit the website <ArrowUpRight size={15} />
            </a>
            <a
              href={REPO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`${btnBase} border border-[var(--baton-hairline)] text-[var(--fg-primary)] hover:bg-[var(--bg-hover)]`}
            >
              <Github size={15} /> GitHub
            </a>
            <Link
              to={CASE_STUDY_URL}
              className={`${btnBase} border border-[var(--baton-hairline)] text-[var(--fg-primary)] hover:bg-[var(--bg-hover)]`}
            >
              Case study <ArrowRight size={15} />
            </Link>
          </div>
        </motion.div>

        {/* Demo, interfaces and install */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          transition={{ delay: 0.08 }}
          className="min-w-0 space-y-5"
        >
          <RecallDemo />

          <dl className="divide-y divide-[var(--baton-hairline)] rounded-xl border border-[var(--baton-hairline)]">
            {INTERFACES.map((row) => (
              <div key={row.label} className="flex flex-col gap-0.5 px-4 py-2.5 sm:flex-row sm:items-baseline sm:gap-4">
                <dt className="baton-mono w-20 shrink-0 text-[11px] uppercase tracking-wider text-[var(--fg-muted)]">{row.label}</dt>
                <dd className="text-sm text-[var(--fg-primary)]">{row.value}</dd>
              </div>
            ))}
          </dl>

          <div>
            <p className="baton-mono mb-1.5 text-[11px] uppercase tracking-wider text-[var(--fg-muted)]">Install from source</p>
            <div className="flex items-start gap-2 rounded-xl border border-[var(--baton-hairline)] bg-[var(--bg-elevated)] px-4 py-3">
              <pre className="baton-mono min-w-0 flex-1 overflow-x-auto whitespace-pre text-xs leading-relaxed text-[var(--fg-primary)] [scrollbar-width:thin]">
                {INSTALL}
              </pre>
              <button
                type="button"
                onClick={copyInstall}
                className="shrink-0 rounded-md p-1.5 text-[var(--fg-secondary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg-primary)] cursor-pointer"
                aria-label="Copy install commands"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}
